'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCart } from '@/context/CartContext';
import { useTranslation } from '@/context/LanguageContext';
import LanguagePicker from '@/components/LanguagePicker';
import styles from './Header.module.scss';

export default function Header() {
  const { totalItems, setCartOpen } = useCart();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [erhvervOpen, setErhvervOpen] = useState(false);
  const [mobileErhvervOpen, setMobileErhvervOpen] = useState(false);
  const erhvervRef = useRef<HTMLDivElement>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setMenuOpen(false);
    setErhvervOpen(false);
    setMobileErhvervOpen(false);
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (erhvervRef.current && !erhvervRef.current.contains(e.target as Node)) {
        setErhvervOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.inner}>
          <Link href="/" className={styles.logo}>
            {t.nav.logo}
          </Link>

          <nav className={styles.desktopNav}>
            <Link href="/shop" className={styles.navLink}>
              {t.nav.shop}
            </Link>
            <Link href="/abonnement" className={styles.navLink}>
              {t.nav.subscription}
            </Link>
            <div
              className={styles.dropdown}
              ref={erhvervRef}
              onMouseEnter={() => {
                if (closeTimeout.current) {
                  clearTimeout(closeTimeout.current);
                  closeTimeout.current = null;
                }
                setErhvervOpen(true);
              }}
              onMouseLeave={() => {
                closeTimeout.current = setTimeout(() => {
                  setErhvervOpen(false);
                  closeTimeout.current = null;
                }, 250);
              }}
            >
              <button
                className={`${styles.navLink} ${styles.dropdownTrigger}`}
                onClick={() => setErhvervOpen(!erhvervOpen)}
                aria-expanded={erhvervOpen}
                aria-haspopup="true"
              >
                {t.nav.business}
                <svg
                  className={`${styles.chevron} ${erhvervOpen ? styles.chevronOpen : ''}`}
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                >
                  <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {erhvervOpen && (
                <div className={styles.dropdownMenu}>
                  <Link href="/erhverv/cafeer" className={styles.dropdownItem}>
                    {t.nav.cafes}
                  </Link>
                  <Link href="/erhverv" className={styles.dropdownItem}>
                    {t.nav.company}
                  </Link>
                </div>
              )}
            </div>
            <Link href="/om" className={styles.navLink}>
              {t.nav.about}
            </Link>
          </nav>

          <div className={styles.actions}>
            <button
              className={styles.cartButton}
              onClick={() => setCartOpen(true)}
              aria-label={t.nav.openCart}
            >
              <svg className={styles.cartIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 01-8 0" />
              </svg>
              <span className={styles.cartCount}>{totalItems}</span>
            </button>
            <button
              className={styles.menuToggle}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t.nav.closeMenu : t.nav.openMenu}
            >
              <span className={`${styles.burger} ${menuOpen ? styles.burgerOpen : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <div
        className={`${styles.mobileNav} ${menuOpen ? styles.mobileNavOpen : ''}`}
        aria-hidden={!menuOpen}
      >
        <nav>
          <Link href="/shop" className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>
            {t.nav.shop}
          </Link>
          <Link href="/abonnement" className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>
            {t.nav.subscription}
          </Link>
          <div className={styles.mobileDropdown}>
            <button
              className={`${styles.mobileNavLink} ${styles.mobileDropdownTrigger}`}
              onClick={() => setMobileErhvervOpen(!mobileErhvervOpen)}
              aria-expanded={mobileErhvervOpen}
            >
              {t.nav.business}
              <svg
                className={`${styles.chevron} ${mobileErhvervOpen ? styles.chevronOpen : ''}`}
                width="12"
                height="7"
                viewBox="0 0 10 6"
                fill="none"
              >
                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className={`${styles.mobileDropdownMenu} ${mobileErhvervOpen ? styles.mobileDropdownMenuOpen : ''}`}>
              <Link href="/erhverv/cafeer" className={styles.mobileDropdownItem} onClick={() => setMenuOpen(false)}>
                {t.nav.cafes}
              </Link>
              <Link href="/erhverv" className={styles.mobileDropdownItem} onClick={() => setMenuOpen(false)}>
                {t.nav.company}
              </Link>
            </div>
          </div>
          <Link href="/om" className={styles.mobileNavLink} onClick={() => setMenuOpen(false)}>
            {t.nav.about}
          </Link>
          <div className={styles.mobileLangPicker}>
            <LanguagePicker />
          </div>
        </nav>
      </div>
    </>
  );
}
